import { Loader } from "@googlemaps/js-api-loader";
import { useEffect, useMemo, useRef, useState } from "react";
import { Input } from "../../../app/components/ui/input";

type PlaceSelection = {
  formattedAddress: string;
  placeId: string | null;
  lat: number | null;
  lng: number | null;
};

export function PlaceAutocompleteInput({
  value,
  onChange,
  onPick,
  placeholder = "Search address",
  disabled,
}: {
  value: string;
  onChange: (next: string) => void;
  onPick: (sel: PlaceSelection) => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  const apiKey = (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined) ?? "";
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [ready, setReady] = useState(false);

  const loader = useMemo(() => {
    if (!apiKey) return null;
    return new Loader({
      apiKey,
      libraries: ["places"],
    });
  }, [apiKey]);

  useEffect(() => {
    let cancelled = false;
    if (!loader) return;

    (async () => {
      try {
        await loader.load();
        if (!cancelled) setReady(true);
      } catch {
        if (!cancelled) setReady(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loader]);

  useEffect(() => {
    if (!ready) return;
    const el = inputRef.current;
    if (!el) return;

    const g = (window as any).google as any;
    if (!g?.maps?.places?.Autocomplete) return;

    const ac = new g.maps.places.Autocomplete(el, {
      fields: ["formatted_address", "geometry", "place_id"],
    });

    const listener = ac.addListener("place_changed", () => {
      const place = ac.getPlace();
      const formattedAddress = String(place?.formatted_address ?? "").trim();
      const placeId = place?.place_id ? String(place.place_id) : null;
      const lat = typeof place?.geometry?.location?.lat === "function" ? Number(place.geometry.location.lat()) : null;
      const lng = typeof place?.geometry?.location?.lng === "function" ? Number(place.geometry.location.lng()) : null;
      if (!formattedAddress) return;
      onPick({ formattedAddress, placeId, lat, lng });
    });

    return () => {
      if (listener?.remove) listener.remove();
    };
  }, [ready, onPick]);

  return (
    <Input
      ref={inputRef}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={!apiKey ? "Google Places API key missing" : placeholder}
      disabled={disabled || !apiKey}
    />
  );
}

